// The next card = a paper card lying on her desk (D_Desk `desk`): lined paper, Kalam hand-writing, laid flat in CSS
// perspective exactly over the white card in the painting (ROOM[ch].cx / by, in picture coordinates), the other cards
// stacked under it, dimmer at night. Hover lifts it and shows its buttons above it; click opens today's spread. It
// stands up as the timer card while working together, shows her first step when you're stuck, and gets the stamp
// (DONE / DAY n / PERFECT / LV n) + "+N" when a card is finished, then is tossed aside and the next one shows.
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { Icon, type IconName } from '../../components';
import { serverNow } from '../day/util';
import { PIC, ROOM } from './roomCopy';
import type { RoomFit } from './roomFit';
import s from './room.module.css';

export interface DeskAct { key: string; label: string; icon?: IconName; run: () => void; strong?: boolean; aria?: string; busy?: boolean }
export interface DeskView {
  kind: 'blank' | 'draft' | 'next' | 'sess' | 'stuck' | 'help' | 'alldone';
  label: string; title: string; foot: string;
  stamp?: string;
  aria: string;
  open?: (() => void) | null;
  /** Cards still under this one (0 = none drawn). */
  stack: number;
  /** Stands up (timer, stuck, help) instead of lying on the desk. */
  posed: boolean;
  /** Buttons shown above the card: always (posed kinds) or on hover (lying kinds). */
  actions: DeskAct[];
  actionsOnHover: boolean;
}
export interface DeskStamp { key: number; label: string; title: string; text: string; pts: number }

interface Props {
  ch: CharId; fit: RoomFit; night: boolean; view: DeskView; lift: boolean; setLift: (on: boolean) => void; stamp: DeskStamp | null;
  /** Working together: when it started (server clock), for the timer on the card. */
  sessFrom?: number | null;
}

const flat = 'perspective(900px) rotateX(50deg)';
const up = 'perspective(900px) rotateX(10deg) translateY(-60px) scale(1.06)';

export function RoomDesk({ ch, fit, night, view, lift, setLift, stamp, sessFrom }: Props) {
  const R = ROOM[ch];
  const ref = useRef<HTMLDivElement>(null);
  // Keyboard: focus lifts the card like hover does; leaving the card (and its buttons) puts it down.
  const blur = (e: FocusEvent) => { if (!ref.current?.contains(e.relatedTarget as Node | null)) setLift(false); };
  const posed = view.posed || lift || !!stamp;
  const h = posed ? 322 : 200;
  const acts = view.actions.length > 0 && !stamp && (!view.actionsOnHover || lift) ? view.actions : [];
  const box: JSX.CSSProperties = { left: fit.px, top: fit.py, width: PIC.w, height: PIC.h, transform: `scale(${fit.p})` };
  const ink = { '--ink': R.ink } as JSX.CSSProperties;
  return (
    <div class={s.deskBox} style={box}>
      <div
        ref={ref} class={s.desk} data-kind={view.kind} data-posed={posed ? 'true' : 'false'}
        style={{ ...ink, left: R.cx - 160, top: R.by - h, height: h, filter: night ? 'brightness(0.8) sepia(0.12)' : 'none' }}
        onMouseEnter={() => setLift(true)} onMouseLeave={() => setLift(false)}
      >
        {view.stack > 0 && (
          <>
            <div class={s.rest} aria-hidden="true" style={{ transform: 'perspective(900px) rotateX(50deg) translate(9px, -8px) rotate(3.5deg)', background: '#DCD1BA' }} />
            <div class={s.rest} aria-hidden="true" style={{ transform: 'perspective(900px) rotateX(50deg) translate(-7px, -4px) rotate(-2.5deg)', background: '#E7DDC8' }} />
          </>
        )}
        <div
          class={s.paper} role={view.open ? 'button' : undefined} tabIndex={view.open ? 0 : undefined} aria-label={view.aria}
          data-open={view.open ? 'true' : 'false'}
          style={{ transform: posed ? up : flat, boxShadow: posed ? '0 26px 50px rgba(0,0,0,0.55)' : '0 6px 14px rgba(0,0,0,0.45)' }}
          onClick={() => view.open?.()}
          onKeyDown={(e) => { if (view.open && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); view.open(); } }}
          onFocus={() => setLift(true)} onBlur={blur}
        >
          <span class={s.paperTop} aria-hidden="true" />
          <span class={s.margin} aria-hidden="true" />
          <span class={s.label}>{view.label}</span>
          <span class={s.title} data-long={view.title.length > 34 ? 'true' : 'false'}>{view.title}</span>
          <span class={s.grow} />
          {sessFrom != null && <DeskClock from={sessFrom} />}
          <span class={s.foot}>{view.foot}</span>
          {view.stamp && <span class={s.stamp} aria-hidden="true">{view.stamp}</span>}
        </div>
        {stamp && (
          <>
            <div key={'out' + stamp.key} class={`${s.paper} ${s.out}`} aria-hidden="true">
              <span class={s.paperTop} />
              <span class={s.margin} />
              <span class={s.label}>{stamp.label}</span>
              <span class={s.title}>{stamp.title}</span>
              <span class={`${s.stamp} ${s.outStamp}`}>{stamp.text}</span>
            </div>
            {stamp.pts > 0 && <div key={'pts' + stamp.key} class={s.pts} aria-hidden="true"><span>{`+${stamp.pts}`}</span></div>}
          </>
        )}
        {acts.length > 0 && (
          <div class={s.acts}>
            {acts.map((a) => (
              <button
                key={a.key} type="button" class={s.act} data-strong={a.strong ? 'true' : 'false'} data-icon-only={a.label ? 'false' : 'true'}
                aria-label={a.aria || a.label} title={a.label ? undefined : a.aria} aria-busy={a.busy ? 'true' : undefined} onClick={a.run} onBlur={blur}
              >
                {a.icon && <Icon name={a.icon} size={a.label ? 15 : 16} strokeWidth={2.6} fill={a.icon === 'play' ? 'currentColor' : undefined} />}
                {a.label && <span>{a.label}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export const clockText = (ms: number) => {
  const x = Math.max(0, Math.floor(ms / 1000)), h = Math.floor(x / 3600), m = Math.floor((x % 3600) / 60), sec = x % 60;
  const p = (n: number) => (n < 10 ? '0' : '') + n;
  return h ? `${h}:${p(m)}:${p(sec)}` : `${p(m)}:${p(sec)}`;
};

/** The timer on the standing card: counts from when the card was started (the server's clock). */
function DeskClock({ from }: { from: number }) {
  const [now, setNow] = useState(serverNow());
  useEffect(() => { const iv = setInterval(() => setNow(serverNow()), 1000); return () => clearInterval(iv); }, []);
  const text = clockText(now - from);
  return <span class={s.clock} role="timer" aria-label={`Working together for ${text}`}>{text}</span>;
}
